import React, { useState, useEffect } from 'react';
import api from '../../utils/api';
import { toast } from 'react-toastify';

const WARDS = [
  'Ward 1', 'Ward 2', 'Ward 3', 'Ward 4', 'Ward 5',
  'Ward 6', 'Ward 7', 'Ward 8', 'Ward 9', 'Ward 10'
];

const RealtorAdsTab = () => {
  const [ads, setAds] = useState([]);
  const [pendingAds, setPendingAds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tableError, setTableError] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingAd, setEditingAd] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [banner, setBanner] = useState({ image_url: '', link_url: '/ads', is_active: false });
  const [bannerUploading, setBannerUploading] = useState(false);
  const [form, setForm] = useState({
    ward: '',
    business_name: '',
    description: '',
    image_url: '',
    cta_label: 'VIEW LISTINGS',
    cta_url: '',
    cta_visible: true,
    expert_name: '',
    is_active: true,
  });

  useEffect(() => { fetchAds(); fetchBanner(); }, []);

  const fetchBanner = async () => {
    try {
      const res = await api.get('/admin/default-ward-banner');
      setBanner(res.data || { image_url: '', link_url: '/ads', is_active: false });
    } catch { }
  };

  const handleBannerUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setBannerUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post('/admin/default-ward-banner/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setBanner(prev => ({ ...prev, image_url: res.data.image_url, is_active: true }));
      toast.success('Banner uploaded');
    } catch {
      toast.error('Upload failed');
    } finally {
      setBannerUploading(false);
    }
  };

  const saveBanner = async () => {
    try {
      const payload = { ...banner };
      if (payload.image_url && !payload.is_active) {
        payload.is_active = true;
      }
      const res = await api.put('/admin/default-ward-banner', payload);
      setBanner(res.data);
      toast.success('Default banner saved');
    } catch {
      toast.error('Failed to save banner');
    }
  };

  const fetchAds = async () => {
    try {
      const res = await api.get('/admin/ward-realtor-ads');
      const all = res.data || [];
      setPendingAds(all.filter(a => a.pending_review && !a.is_active));
      setAds(all);
      setTableError(false);
    } catch (err) {
      if (err.response?.status === 500 || err.response?.data?.detail?.includes('ward_realtor_ads')) {
        setTableError(true);
      }
      setAds([]);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setForm({
      ward: '', business_name: '', description: '', image_url: '',
      cta_label: 'VIEW LISTINGS', cta_url: '', cta_visible: true,
      expert_name: '', is_active: true,
    });
    setEditingAd(null);
  };

  const openCreate = () => {
    resetForm();
    setShowModal(true);
  };

  const openEdit = (ad) => {
    setEditingAd(ad.id);
    setForm({
      ward: ad.ward || '',
      business_name: ad.business_name || '',
      description: ad.description || '',
      image_url: ad.image_url || '',
      cta_label: ad.cta_label || 'VIEW LISTINGS',
      cta_url: ad.cta_url || '',
      cta_visible: ad.cta_visible !== false,
      expert_name: ad.expert_name || '',
      is_active: ad.is_active !== false,
    });
    setShowModal(true);
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post('/admin/ward-realtor-ads/upload-image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setForm(prev => ({ ...prev, image_url: res.data.image_url }));
      toast.success('Image uploaded');
    } catch (err) {
      toast.error('Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.ward || !form.business_name) {
      toast.error('Ward and Business Name are required');
      return;
    }
    try {
      if (editingAd) {
        const { ward, ...updateData } = form;
        await api.put(`/admin/ward-realtor-ads/${editingAd}`, updateData);
        toast.success('Realtor ad updated');
      } else {
        await api.post('/admin/ward-realtor-ads', form);
        toast.success('Realtor ad created');
      }
      setShowModal(false);
      resetForm();
      fetchAds();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    }
  };

  const handleDelete = async (adId) => {
    if (!window.confirm('Delete this realtor ad?')) return;
    try {
      await api.delete(`/admin/ward-realtor-ads/${adId}`);
      toast.success('Deleted');
      fetchAds();
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  const toggleActive = async (ad) => {
    try {
      await api.put(`/admin/ward-realtor-ads/${ad.id}`, { is_active: !ad.is_active });
      toast.success(ad.is_active ? 'Ad hidden' : 'Ad visible');
      fetchAds();
    } catch (err) {
      toast.error('Failed to update');
    }
  };

  const usedWards = Array.isArray(ads) ? ads.map((ad) => ad.ward).filter(Boolean) : [];
  const wardOptions = WARDS;

  if (loading) return <p className="text-gray-500 py-8 text-center">Loading realtor ads...</p>;

  if (tableError) {
    return (
      <div className="bg-red-900/20 border border-red-800 p-6 rounded text-center">
        <h3 className="text-red-400 font-bold text-lg mb-2">Table Not Found</h3>
        <p className="text-gray-400 mb-4">
          The <code className="bg-gray-800 px-2 py-1 rounded text-yellow-400">ward_realtor_ads</code> table needs to be created in Supabase.
        </p>
        <p className="text-gray-500 text-sm">
          Run the SQL in <code className="bg-gray-800 px-2 py-1 rounded text-green-400">/app/create_ward_realtor_ads_table.sql</code> in your Supabase SQL Editor.
        </p>
      </div>
    );
  }

  return (
    <div>
      {/* PENDING REVIEW ALERT */}
      {pendingAds.length > 0 && (
        <div className="bg-yellow-900/20 border border-yellow-700/50 rounded-sm p-4 mb-6">
          <div className="flex items-start gap-3">
            <span className="text-yellow-400 text-xl mt-0.5">⏳</span>
            <div className="flex-1">
              <p className="text-yellow-400 font-bold mb-2">
                {pendingAds.length} Realtor Ad{pendingAds.length > 1 ? 's' : ''} Awaiting Your Review
              </p>
              <div className="space-y-2">
                {pendingAds.map(ad => (
                  <div key={ad.id} className="flex items-center justify-between bg-yellow-950/30 border border-yellow-900/40 px-4 py-2 rounded-sm">
                    <div>
                      <span className="text-white font-bold text-sm">{ad.ward}</span>
                      <span className="text-gray-400 text-xs ml-3">— {ad.expert_name || ad.business_name}</span>
                      {ad.email && <span className="text-gray-600 text-xs ml-2">({ad.email})</span>}
                    </div>
                    <button
                      onClick={async () => {
                        try {
                          await api.put(`/admin/ward-realtor-ads/${ad.id}`, { ...ad, is_active: true, pending_review: false });
                          toast.success(`${ad.ward} ad is now live!`);
                          fetchAds();
                        } catch { toast.error('Failed to activate'); }
                      }}
                      className="text-xs font-mono uppercase tracking-wider bg-green-600 hover:bg-green-500 text-white px-3 py-1.5 transition-colors"
                    >
                      ✓ Activate
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* DEFAULT BANNER SECTION */}
      <div className="bg-gray-900 border border-gray-800 rounded-sm p-6 mb-8">
        <div className="flex justify-between items-start mb-4">
          <div>
            <h3 className="text-lg font-bold text-white">Default Ward Banner</h3>
            <p className="text-gray-400 text-sm">Shows on ward pages that don't have a realtor ad yet. One image, all unsold wards.</p>
          </div>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={banner.is_active}
              onChange={(e) => setBanner({ ...banner, is_active: e.target.checked })}
              className="rounded bg-gray-700 border-gray-600"
              data-testid="default-banner-active"
            />
            <span className="text-gray-300 text-sm font-bold">{banner.is_active ? 'Active' : 'Hidden'}</span>
          </label>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-gray-500 text-xs mb-2 uppercase tracking-wider">Banner Image</label>
            <input
              type="file"
              accept="image/*"
              onChange={handleBannerUpload}
              className="text-sm text-gray-400 file:mr-3 file:py-1.5 file:px-4 file:rounded-sm file:border-0 file:text-xs file:font-mono file:uppercase file:bg-gray-700 file:text-white hover:file:bg-gray-600 mb-2"
              data-testid="default-banner-upload"
            />
            {bannerUploading && <span className="text-yellow-400 text-xs">Uploading...</span>}
            {banner.image_url && (
              <img src={banner.image_url} alt="Default banner" className="mt-2 max-h-32 object-contain rounded border border-gray-800" />
            )}
          </div>
          <div className="space-y-3">
            <div>
              <label className="block text-gray-500 text-xs mb-1 uppercase tracking-wider">Click-through Link</label>
              <input
                type="text"
                value={banner.link_url}
                onChange={(e) => setBanner({ ...banner, link_url: e.target.value })}
                placeholder="/ads"
                className="w-full bg-gray-800 border border-gray-700 text-white px-3 py-2 rounded-sm text-sm"
                data-testid="default-banner-link"
              />
            </div>
            <button
              onClick={saveBanner}
              className="bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-6 rounded-sm uppercase text-xs tracking-wider"
              data-testid="save-default-banner-btn"
            >
              Save Banner Settings
            </button>
          </div>
        </div>
      </div>

      {/* WARD REALTOR ADS SECTION */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold text-white mb-1">Ward Realtor Ads</h2>
          <p className="text-gray-400 text-sm">One exclusive realtor per ward page. This is the money maker.</p>
        </div>
        <button
          onClick={openCreate}
          className="bg-yellow-500 hover:bg-yellow-600 text-black font-bold py-2 px-6 rounded-sm uppercase tracking-wider text-sm"
          data-testid="create-realtor-ad-btn"
        >
          + New Realtor Ad
        </button>
      </div>

      {/* Ads Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {ads.length === 0 ? (
          <div className="col-span-2 text-center py-12 text-gray-500">
            No realtor ads yet. Create one for each ward.
          </div>
        ) : (
          ads.map((ad) => (
            <div
              key={ad.id}
              className={`bg-gray-900 border rounded-sm p-5 ${
                ad.pending_review && !ad.is_active
                  ? 'border-yellow-700/60 bg-yellow-950/10'
                  : ad.is_active
                    ? 'border-gray-800'
                    : 'border-red-900/50 opacity-60'
              }`}
              data-testid={`realtor-ad-card-${ad.ward}`}
            >
              <div className="flex justify-between items-start mb-3">
                <div>
                  <span className="text-yellow-400 text-xs font-bold uppercase tracking-wider">{ad.ward}</span>
                  <h3 className="text-white font-bold text-lg">{ad.business_name || <span className="text-gray-500 italic text-sm">Business name not set yet</span>}</h3>
                  {ad.expert_name && (
                    <p className="text-gray-400 text-sm">Featured: <span className="text-yellow-400">{ad.expert_name}</span></p>
                  )}
                  {/* Show contact info for pending ads */}
                  {ad.pending_review && !ad.is_active && (
                    <div className="mt-1 space-y-0.5">
                      {ad.email && <p className="text-xs text-gray-400">📧 {ad.email}</p>}
                      {ad.phone && <p className="text-xs text-gray-400">📞 {ad.phone}</p>}
                      {ad.plan && <p className="text-xs text-blue-400 font-mono uppercase">Plan: {ad.plan}</p>}
                    </div>
                  )}
                </div>
                <span className={`text-xs font-mono px-2 py-1 rounded ${
                  ad.pending_review && !ad.is_active
                    ? 'bg-yellow-900/50 text-yellow-400 border border-yellow-700/50'
                    : ad.is_active
                      ? 'bg-green-900/30 text-green-400'
                      : 'bg-red-900/30 text-red-400'
                }`}>
                  {ad.pending_review && !ad.is_active ? '⏳ SETUP PENDING' : ad.is_active ? 'ACTIVE' : 'HIDDEN'}
                </span>
              </div>

              {ad.description && <p className="text-gray-500 text-sm mb-2 line-clamp-2">{ad.description}</p>}

              <div className="flex items-center gap-3 mb-2 text-[10px] text-gray-600 font-mono">
                <span>Added {ad.created_at ? new Date(ad.created_at).toLocaleDateString('en-CA', { year: 'numeric', month: 'short', day: 'numeric' }) : '—'}</span>
              </div>

              <div className="flex items-center gap-3 mb-3 text-xs text-gray-500">
                {ad.image_url && <span className="text-green-400">Has Photo</span>}
                {ad.cta_url && (
                  <span className={ad.cta_visible ? 'text-blue-400' : 'text-gray-600'}>
                    CTA: {ad.cta_label} {!ad.cta_visible && '(hidden)'}
                  </span>
                )}
              </div>

              <div className="flex gap-2">
                <button
                  onClick={() => openEdit(ad)}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-1.5 rounded-sm text-xs font-mono uppercase"
                >
                  Edit
                </button>
                <button
                  onClick={() => toggleActive(ad)}
                  className={`px-4 py-1.5 rounded-sm text-xs font-mono uppercase ${ad.is_active ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' : 'bg-green-700 hover:bg-green-600 text-white'}`}
                >
                  {ad.is_active ? 'Hide' : 'Show'}
                </button>
                <button
                  onClick={() => handleDelete(ad.id)}
                  className="bg-red-600 hover:bg-red-700 text-white px-4 py-1.5 rounded-sm text-xs font-mono uppercase"
                >
                  Delete
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-900 border border-gray-800 rounded-sm p-6 max-w-xl w-full max-h-[90vh] overflow-y-auto">
            <h3 className="text-xl font-bold text-white mb-4">
              {editingAd ? 'Edit Realtor Ad' : 'Create Realtor Ad'}
            </h3>

            <form onSubmit={handleSave} className="space-y-4">
              {/* Ward */}
              <div>
                <label className="block text-gray-400 text-sm mb-1">Ward Assignment</label>
                <select
                  value={form.ward}
                  onChange={(e) => setForm({ ...form, ward: e.target.value })}
                  disabled={!!editingAd}
                  className="w-full bg-gray-800 border border-gray-700 text-white px-3 py-2 rounded-sm disabled:opacity-50"
                  required
                  data-testid="realtor-ad-ward-select"
                >
                  <option value="">Select Ward</option>
                  <option value="Ward 1" disabled={!editingAd && usedWards.includes('Ward 1')}>Ward 1{!editingAd && usedWards.includes('Ward 1') ? ' (Taken)' : ''}</option>
                  <option value="Ward 2" disabled={!editingAd && usedWards.includes('Ward 2')}>Ward 2{!editingAd && usedWards.includes('Ward 2') ? ' (Taken)' : ''}</option>
                  <option value="Ward 3" disabled={!editingAd && usedWards.includes('Ward 3')}>Ward 3{!editingAd && usedWards.includes('Ward 3') ? ' (Taken)' : ''}</option>
                  <option value="Ward 4" disabled={!editingAd && usedWards.includes('Ward 4')}>Ward 4{!editingAd && usedWards.includes('Ward 4') ? ' (Taken)' : ''}</option>
                  <option value="Ward 5" disabled={!editingAd && usedWards.includes('Ward 5')}>Ward 5{!editingAd && usedWards.includes('Ward 5') ? ' (Taken)' : ''}</option>
                  <option value="Ward 6" disabled={!editingAd && usedWards.includes('Ward 6')}>Ward 6{!editingAd && usedWards.includes('Ward 6') ? ' (Taken)' : ''}</option>
                  <option value="Ward 7" disabled={!editingAd && usedWards.includes('Ward 7')}>Ward 7{!editingAd && usedWards.includes('Ward 7') ? ' (Taken)' : ''}</option>
                  <option value="Ward 8" disabled={!editingAd && usedWards.includes('Ward 8')}>Ward 8{!editingAd && usedWards.includes('Ward 8') ? ' (Taken)' : ''}</option>
                  <option value="Ward 9" disabled={!editingAd && usedWards.includes('Ward 9')}>Ward 9{!editingAd && usedWards.includes('Ward 9') ? ' (Taken)' : ''}</option>
                  <option value="Ward 10" disabled={!editingAd && usedWards.includes('Ward 10')}>Ward 10{!editingAd && usedWards.includes('Ward 10') ? ' (Taken)' : ''}</option>
                </select>
              </div>

              {/* Business Name */}
              <div>
                <label className="block text-gray-400 text-sm mb-1">Business Name</label>
                <input
                  type="text"
                  value={form.business_name}
                  onChange={(e) => setForm({ ...form, business_name: e.target.value })}
                  placeholder="REGINA ELITE REALTY"
                  className="w-full bg-gray-800 border border-gray-700 text-white px-3 py-2 rounded-sm"
                  required
                  data-testid="realtor-ad-business-name"
                />
              </div>

              {/* Description / Wording */}
              <div>
                <label className="block text-gray-400 text-sm mb-1">
                  Ad Copy / Wording <span className="text-gray-600 text-xs">(shown on ward page &amp; dashboard)</span>
                </label>
                <textarea
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  rows={3}
                  placeholder="Specializing in Ward 1's premium residential properties..."
                  className="w-full bg-gray-800 border border-gray-700 text-white px-3 py-2 rounded-sm resize-none"
                  data-testid="realtor-ad-description"
                />
              </div>

              {/* Image Upload */}
              <div>
                <label className="block text-gray-400 text-sm mb-1">Realtor Photo</label>
                <div className="flex items-center gap-3">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="text-sm text-gray-400 file:mr-3 file:py-1.5 file:px-4 file:rounded-sm file:border-0 file:text-xs file:font-mono file:uppercase file:bg-gray-700 file:text-white hover:file:bg-gray-600"
                    data-testid="realtor-ad-image-upload"
                  />
                  {uploading && <span className="text-yellow-400 text-xs">Uploading...</span>}
                </div>
                {form.image_url && (
                  <div className="mt-2">
                    <img src={form.image_url} alt="Preview" className="h-20 object-cover rounded" />
                  </div>
                )}
                <input
                  type="text"
                  value={form.image_url}
                  onChange={(e) => setForm({ ...form, image_url: e.target.value })}
                  placeholder="Or paste image URL directly"
                  className="w-full bg-gray-800 border border-gray-700 text-white px-3 py-2 rounded-sm mt-2 text-xs"
                />
              </div>

              {/* CTA Button */}
              <div className="bg-gray-800/50 p-4 rounded space-y-3">
                <div className="flex items-center justify-between">
                  <label className="text-gray-300 text-sm font-bold">Call-to-Action Button</label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.cta_visible}
                      onChange={(e) => setForm({ ...form, cta_visible: e.target.checked })}
                      className="rounded bg-gray-700 border-gray-600"
                      data-testid="realtor-ad-cta-visible"
                    />
                    <span className="text-gray-400 text-xs">Visible</span>
                  </label>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-gray-500 text-xs mb-1">Button Label</label>
                    <input
                      type="text"
                      value={form.cta_label}
                      onChange={(e) => setForm({ ...form, cta_label: e.target.value })}
                      placeholder="VIEW WARD 1 LISTINGS"
                      className="w-full bg-gray-800 border border-gray-700 text-white px-3 py-2 rounded-sm text-sm"
                      data-testid="realtor-ad-cta-label"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-500 text-xs mb-1">Link URL</label>
                    <input
                      type="text"
                      value={form.cta_url}
                      onChange={(e) => setForm({ ...form, cta_url: e.target.value })}
                      placeholder="https://realtor.ca/ward-1"
                      className="w-full bg-gray-800 border border-gray-700 text-white px-3 py-2 rounded-sm text-sm"
                      data-testid="realtor-ad-cta-url"
                    />
                  </div>
                </div>
              </div>

              {/* Expert Name */}
              <div>
                <label className="block text-gray-400 text-sm mb-1">Featured Expert Name</label>
                <input
                  type="text"
                  value={form.expert_name}
                  onChange={(e) => setForm({ ...form, expert_name: e.target.value })}
                  placeholder="JULIAN VANCE"
                  className="w-full bg-gray-800 border border-gray-700 text-white px-3 py-2 rounded-sm"
                  data-testid="realtor-ad-expert-name"
                />
              </div>

              {/* Active Toggle */}
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.is_active}
                  onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                  className="rounded bg-gray-700 border-gray-600"
                  data-testid="realtor-ad-is-active"
                />
                <span className="text-gray-300 text-sm">Active (visible on ward page)</span>
              </label>

              {/* Actions */}
              <div className="flex gap-3 pt-2">
                <button
                  type="submit"
                  className="bg-yellow-500 hover:bg-yellow-600 text-black font-bold px-6 py-2 rounded-sm uppercase text-sm tracking-wider"
                  data-testid="save-realtor-ad-btn"
                >
                  {editingAd ? 'Update' : 'Create'}
                </button>
                <button
                  type="button"
                  onClick={() => { setShowModal(false); resetForm(); }}
                  className="bg-gray-700 hover:bg-gray-600 text-white px-6 py-2 rounded-sm uppercase text-sm"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default RealtorAdsTab;
